import React, { useMemo, useState } from 'react';
import { FiArrowLeft, FiCheck, FiLock } from 'react-icons/fi';
import { FOCUS } from '../../components/ui/tokens';
import type { DocumentTrailDto } from '../../lib/rfqLifecycleApi';
import { buildTrail, lockNote, sidebarSteps, type TrailBox } from './lifecycleModel';
import { BOX_OF_TYPE, type TrailTarget } from './trailTarget';

export interface SidebarTrailProps {
  trail: DocumentTrailDto;
  /** The document whose page is open: its step is highlighted. */
  current: TrailTarget;
  /** Whether this person may open documents of the step's kind (the page's own permission). */
  canOpen: (box: TrailBox) => boolean;
  onOpen: (to: string) => void;
  onHome: () => void;
}

const ROW = 'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition';

// A step with more records than this shows this many and scrolls the rest inside itself, so ten POs do not
// stretch the sidebar. One record is 48px tall (two lines) plus a 2px gap.
export const TRAIL_VISIBLE_RECORDS = 5;
const RECORD_PX = 50;

/**
 * The open document's whole cycle, drawn in the sidebar in place of the first part of the menu:
 * RFQ, Quotation, Order, Vendor PO, Vendor Invoice, Client Invoice, Credit Note. A tick means the step
 * exists and opens that document; a lock means it does not exist yet, and pressing it says why. Draws what
 * `buildTrail` returned (the same words and links as the Home trail) and decides nothing itself.
 */
const SidebarTrail: React.FC<SidebarTrailProps> = ({ trail, current, canOpen, onOpen, onHome }) => {
  const steps = useMemo(() => sidebarSteps(buildTrail(trail.rfq, trail.lifecycle)), [trail]);
  const currentBox = BOX_OF_TYPE[current.type];
  const [note, setNote] = useState<string | null>(null); // the locked step whose reason is showing
  const vessel = trail.rfq.vesselName.trim();

  return (
    <div>
      <button
        type="button"
        onClick={onHome}
        className={`flex w-full items-center gap-2 border-b border-slate-200 px-3 py-2.5 text-sm font-semibold text-primary-700 hover:bg-slate-50 ${FOCUS}`}
      >
        <FiArrowLeft aria-hidden="true" size={16} />
        Home
      </button>

      <div className="px-4 pb-1 pt-3">
        <div className="truncate text-[15px] font-bold text-slate-900">
          {vessel || <span className="font-normal text-slate-400">Vessel not named</span>}
        </div>
        <div className="truncate text-xs text-slate-500">{trail.rfq.customerName || trail.rfq.rfqId}</div>
      </div>

      <ol aria-label={`Cycle of ${trail.rfq.rfqId || 'this RFQ'}`} className="px-2 pb-3 pt-1">
        {steps.map((box, i) => {
          const filled = box.state === 'filled';
          const next = steps[i + 1];
          const openable = filled && canOpen(box);
          const multi = box.docs.length > 1;
          const isCurrentBox = box.key === currentBox;
          const circle = filled ? (
            <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
              <FiCheck aria-hidden="true" size={14} strokeWidth={3} />
            </span>
          ) : (
            <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-400">
              <FiLock aria-hidden="true" size={12} />
            </span>
          );
          // The line down to the next step is blue only between two steps that both exist.
          const line =
            i < steps.length - 1 ? (
              <span
                aria-hidden="true"
                className={`absolute left-[23px] top-[38px] -bottom-2 w-0.5 ${filled && next.state === 'filled' ? 'bg-primary-600' : 'bg-slate-200'}`}
              />
            ) : null;

          if (!filled || !openable) {
            // Locked: pressing it explains why. No page change.
            const shown = note === box.key;
            return (
              <li key={box.key} className="relative">
                {line}
                <button
                  type="button"
                  aria-expanded={shown}
                  onClick={() => setNote(shown ? null : box.key)}
                  className={`${ROW} mb-2 hover:bg-slate-50 ${FOCUS}`}
                >
                  {circle}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-600">{box.label}</span>
                    {filled ? <span className="block truncate text-xs text-slate-500">{box.value}</span> : null}
                  </span>
                </button>
                {shown ? (
                  <p role="status" className="-mt-1 mb-2 ml-12 mr-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
                    {filled ? `You do not have access to ${box.label.toLowerCase()} records.` : lockNote(box)}
                  </p>
                ) : null}
              </li>
            );
          }

          if (!multi) {
            return (
              <li key={box.key} className="relative">
                {line}
                <button
                  type="button"
                  aria-current={isCurrentBox ? 'page' : undefined}
                  onClick={() => onOpen(box.docs[0].to)}
                  className={`${ROW} mb-2 ${isCurrentBox ? 'bg-primary-50' : 'hover:bg-slate-50'} ${FOCUS}`}
                >
                  {circle}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">{box.label}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {box.sub ? `${box.value} · ${box.sub}` : box.value}
                    </span>
                  </span>
                </button>
              </li>
            );
          }

          return (
            <li key={box.key} className="relative">
              {line}
              <div className={ROW}>
                {circle}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-900">{box.label}</span>
                  <span className="block truncate text-xs text-slate-500">{box.docs.length} created</span>
                </span>
              </div>
              <ul
                aria-label={`${box.label} records`}
                className={`mb-2 ml-12 mr-1 space-y-0.5 ${box.docs.length > TRAIL_VISIBLE_RECORDS ? 'overflow-y-auto overscroll-contain pr-1' : ''}`}
                style={box.docs.length > TRAIL_VISIBLE_RECORDS ? { maxHeight: TRAIL_VISIBLE_RECORDS * RECORD_PX - 2 } : undefined}
              >
                {box.docs.map((doc) => {
                  const here = isCurrentBox && doc.id === current.id;
                  return (
                    <li key={doc.id}>
                      <button
                        type="button"
                        aria-current={here ? 'page' : undefined}
                        onClick={() => onOpen(doc.to)}
                        className={`w-full rounded-md px-2.5 py-1.5 text-left ${here ? 'bg-primary-50' : 'hover:bg-slate-50'} ${FOCUS}`}
                      >
                        <span className="block truncate text-sm font-semibold text-slate-900">{doc.title}</span>
                        {doc.detail ? <span className="block truncate text-xs text-slate-500">{doc.detail}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default SidebarTrail;
